// react
import { useEffect, useRef, useState } from 'react';

// components
import ShortcutsDialog from 'components/shared/ShortcutProvider/components/ShortcutDialog/ShortcutDialog';

// constants
import { GLOBAL_CONTEXT } from './ShortcutProvider.consts';

// context
import { ShortcutContext } from './ShortcutContext';

// hooks
import { useTranslation } from 'react-i18next';

// types
import type { ReactNode } from 'react';
import type { ShortcutAction, ShortcutStore } from './ShortcutProvider.types';

// utils
import {
  addNarrowing,
  getActiveShortcuts,
  handleKeyDown,
  removeNarrowing,
  removeShortcuts,
  setShortcut,
  setShortcutsActive
} from './ShortcutProvider.utils';

export const ShortcutProvider = ({ children }: { children?: ReactNode }) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  // The store is deliberately a ref: it is mutated from effects and read from the window
  // key handler, never during render. It is not put on the context either — handing a
  // mutable registry to consumers meant reading `ref.current` while rendering, which is
  // impure and makes the React Compiler refuse to optimize this provider.
  //
  // Nesting is why the narrowings are a list rather than a single value: the appointment form
  // narrows to the global context, a popover inside that form narrows further, and keeping
  // both is what lets the popover put back what the form had rather than re-activating
  // everything behind it -- which is what a plain "restore all" did, handing calendar
  // navigation back while the form was still open.
  const store = useRef<ShortcutStore>({
    contexts: {},
    narrowings: [],
    lastFiredAt: {}
  });

  // Snapshot of the shortcuts to list, taken when the dialog is opened. Holding the
  // snapshot as state keeps the mutable registry from being read while rendering.
  const [shortcutInfo, setShortcutInfo] = useState<ShortcutAction[] | null>(
    null
  );

  // A single delegated listener handles every shortcut: the registry is searched at
  // keypress time, so registering a shortcut never touches the DOM. Attached once, since
  // the handler reads everything it needs from the store.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) =>
      handleKeyDown(store.current, event);

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Registered from an effect rather than seeded into the store, so the labels follow a
  // language switch instead of freezing at whatever the first render produced.
  useEffect(() => {
    setShortcut(store.current, {
      context: GLOBAL_CONTEXT,
      name: 'info',
      label: t('shortcuts.info.label'),
      description: t('shortcuts.info.description'),
      activatorKey: '?',
      controllerKeys: ['SHIFT'],
      callback: () => setShortcutInfo(getActiveShortcuts(store.current))
    });
  }, [t]);

  const contextValue = {
    disableShortcut: (context: string, name?: string) =>
      setShortcutsActive(store.current, false, context, name),
    enableShortcut: (context: string, name?: string) =>
      setShortcutsActive(store.current, true, context, name),
    pushNarrowing: (id: string, contexts: string[]) =>
      addNarrowing(store.current, id, contexts),
    popNarrowing: (id: string) => removeNarrowing(store.current, id),
    register: (shortcut: ShortcutAction & { context: string }) =>
      setShortcut(store.current, shortcut),
    unregister: (context: string, name?: string) =>
      removeShortcuts(store.current, context, name)
  };

  return (
    <ShortcutContext.Provider value={contextValue}>
      {shortcutInfo && (
        <ShortcutsDialog
          onClose={() => setShortcutInfo(null)}
          shortcuts={shortcutInfo}
        />
      )}

      {children}
    </ShortcutContext.Provider>
  );
};
